import React from "react";
import { SearchableDropdown } from "../../../ui/SearchableDropdown";
import { FilterButton } from "./FilterButton";
import { CheckCircleIcon } from "@heroicons/react/20/solid";

interface SearchableCriteriaDropdownProps {
  ref: React.RefObject<HTMLDivElement>;
  isExpanded: boolean;
  onToggle: () => void;
  values: string[];
  selectedValues: Set<string>;
  isLoading: boolean;
  isActive: boolean;
  onSelectValue: (value: string) => void;
  onApply: () => void;
  onCancel: () => void;
  onRemove?: () => void;
  canApply: boolean;
}

/**
 * Searchable dropdown component for criteria selection with fuzzy search.
 * Allows users to search through potentially large lists of criteria.
 * 
 * This is a specialized wrapper around the generic SearchableDropdown component
 * that provides criteria-specific labels and styling.
 */
const SearchableCriteriaDropdown = React.forwardRef<
  HTMLDivElement,
  Omit<SearchableCriteriaDropdownProps, "ref">
>(
  (
    {
      isExpanded,
      onToggle,
      values,
      selectedValues,
      isLoading,
      isActive,
      onSelectValue,
      onApply,
      onCancel,
      onRemove,
      canApply,
    },
    ref
  ) => {
    // Build label
    const label = "Criteria";

    return (
      <div ref={ref}>
        <SearchableDropdown
          isExpanded={isExpanded}
          onToggle={onToggle}
          values={values}
          selectedValues={selectedValues}
          onSelectValue={onSelectValue}
          onApply={onApply}
          onCancel={onCancel}
          isLoading={isLoading}
          searchPlaceholder="Search criteria..."
          emptyMessage="No criteria available"
          noResultsMessage={(searchTerm) => `No criteria match "${searchTerm}"`}
          minWidth="350px"
          maxWidth="500px"
          maxHeight="256px"
          canApply={canApply}
          renderTrigger={({ isExpanded, onClick }) => (
            <FilterButton
              label={label}
              isActive={isActive}
              isExpanded={isExpanded}
              onClick={onClick}
              onRemove={isActive ? onRemove : undefined}
              icon={<CheckCircleIcon className="h-4 w-4" />}
              badge={isActive && selectedValues.size > 0 ? selectedValues.size : undefined}
            />
          )}
          renderSelectedCount={(count) => `${count} criteria selected`}
        />
      </div>
    );
  }
);

SearchableCriteriaDropdown.displayName = "SearchableCriteriaDropdown";

export { SearchableCriteriaDropdown };
